import { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default Leaflet icon
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

export interface Transaction {
  _id: string;
  date: string;
  price: number;
  currency: string;
  propertyType: string;
  area?: number;
  location: {
    address: string;
    city?: string;
    country?: string;
    coordinates: { lat: number; lng: number };
  };
  cadastralData?: {
    rol?: string;
    yearBuilt?: number;
    [key: string]: any;
  };
}

interface RealEstateMapProps {
  transactions: Transaction[];
  onMapLoad?: (map: L.Map) => void;
  onMarkerClick?: (transaction: Transaction) => void;
}

// Custom marker icon for transactions
const createTransactionIcon = () => {
  return L.divIcon({
    className: 'custom-div-icon',
    html: `<div style="background-color: #D94E33; width: 16px; height: 16px; border-radius: 50%; border: 2px solid #0F0F0F; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
    popupAnchor: [0, -8]
  });
};

function MapController({ transactions, onMapLoad }: { 
  transactions: Transaction[], 
  onMapLoad?: (map: L.Map) => void 
}) {
  const map = useMap();

  useEffect(() => {
    if (onMapLoad) {
      onMapLoad(map);
    }
  }, [map, onMapLoad]);

  useEffect(() => {
    if (transactions.length > 0) {
      const bounds = L.latLngBounds(transactions
        .filter(t => t.location?.coordinates?.lat && t.location?.coordinates?.lng)
        .map(t => [t.location.coordinates.lat, t.location.coordinates.lng] as [number, number])
      );
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
      }
    }
  }, [transactions, map]);

  return null;
}

export function RealEstateMap({ 
  transactions, 
  onMapLoad,
  onMarkerClick
}: RealEstateMapProps) {
  return (
    <div className="w-full h-full border-2 border-ink shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] relative z-0">
      <MapContainer 
        center={[-33.4489, -70.6693]} 
        zoom={12} 
        style={{ height: '100%', width: '100%' }}
        className="z-0"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        <MapController transactions={transactions} onMapLoad={onMapLoad} />

        {transactions.map((t) => {
          if (!t.location?.coordinates?.lat || !t.location?.coordinates?.lng) return null;

          return (
            <Marker 
              key={t._id} 
              position={[t.location.coordinates.lat, t.location.coordinates.lng]}
              icon={createTransactionIcon()}
              eventHandlers={{
                click: () => onMarkerClick?.(t)
              }}
            >
              <Popup className="brutalist-popup">
                <div className="brutalist-border bg-parchment p-4" style={{ width: '240px', fontFamily: "'Schibsted Grotesk', sans-serif" }}>
                  <span className="bg-ink text-parchment px-2 py-0.5 text-[8px] font-black uppercase tracking-widest mb-2 inline-block">Transacción CBR</span>
                  <h3 className="font-display font-black uppercase italic text-sm mb-1">{t.propertyType}</h3>
                  <p className="text-[10px] font-serif italic text-ink/60 mb-3">{t.location.address}</p>
                  <div className="flex justify-between items-end border-t border-ink/10 pt-3">
                    <div>
                      <p className="text-[8px] font-black uppercase opacity-40">Precio de Venta</p>
                      <p className="text-lg font-display font-black text-terracotta">{t.currency} {t.price.toLocaleString()}</p>
                    </div>
                    <p className="text-[8px] font-black opacity-40">{new Date(t.date).toLocaleDateString()}</p>
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>

      {/* Style for matching brutalist theme in popups */}
      <style dangerouslySetInnerHTML={{ __html: `
        .leaflet-popup-content-wrapper {
          border-radius: 0 !important;
          padding: 0 !important;
          border: 2px solid #0F0F0F !important;
          box-shadow: 4px 4px 0px 0px rgba(0,0,0,1) !important;
          background-color: #F5F2ED !important;
        }
        .leaflet-popup-content {
          margin: 0 !important;
          width: auto !important;
        }
        .leaflet-popup-tip {
          background-color: #0F0F0F !important;
        }
      `}} />
    </div>
  );
}
